import React, { useEffect, useMemo, useState } from 'react';
import { User, UserX } from 'lucide-react';
import { FilterButton, FilterDropdown } from '../ui';
import { UserFilter } from './types/dashboardTypes';
import { formatUserDisplayName } from '../../lib/utils';
import { useViewport } from '../../contexts/ViewportContext';
import { useProjects } from '../../hooks/queries/useProjects';
import { logger } from '../../utils/logger';
import { FILTER_CONSTANTS } from './constants/filterConstants';
import { useDashboardContent } from '../../hooks/queries/useDashboard';

interface UserFilterDropdownProps {
  userFilters: UserFilter[];
  isOpen: boolean;
  setDropdownState: (newState?: boolean) => void;
  onSelect: (userId: string, isExclusive: boolean) => void;
  isMobile: boolean;
  activeFilters: { userIds: string[] };
}

/**
 * Dashboard user filter dropdown component that allows filtering tasks by user/assignee
 * Used in the Dashboard header to filter dashboard content by task assignees
 */
const DashboardUserFilterDropdown: React.FC<UserFilterDropdownProps> = ({
  userFilters,
  isOpen,
  setDropdownState,
  onSelect,
  isMobile,
  activeFilters,
}) => {
  // ===== HOOKS - ALL MUST BE AT TOP LEVEL =====
  const { isMobile: isViewportMobile } = useViewport();
  const [hasSharedUnassignedTasks, setHasSharedUnassignedTasks] = useState(false);

  // Access dashboard data using React Query
  const { data: dashboardData = { tasks: [] } } = useDashboardContent();
  const { data: projects = [] } = useProjects();
  const actualMobile = isMobile || isViewportMobile;

  // Extract values from the data
  const rawTasks = dashboardData.tasks || [];
  const isSharedUnassignedActive = activeFilters?.userIds?.includes(FILTER_CONSTANTS.SHARED_UNASSIGNED) || false;

  // Memoize the check for shared & unassigned tasks to avoid unnecessary calculations
  const checkForSharedUnassignedTasks = useMemo(() => {
    // Skip the check if we don't have data yet
    if (!rawTasks.length || !projects.length) return false;

    // Check if any task meets these criteria:
    // 1. Is unassigned (assigned_to is null/undefined)
    // 2. Has a deadline
    // 3. Belongs to a shared project
    return rawTasks.some(task => {
      if (task.assigned_to || !task.deadline) return false;

      const topicInfo = task.topics as { project_id: string; id: string };
      if (!topicInfo?.project_id) return false;

      const project = projects.find(p => p.id === topicInfo.project_id);
      return project?.isShared === true;
    });
  }, [rawTasks, projects]);

  // Update state when the check result changes
  useEffect(() => {
    setHasSharedUnassignedTasks(checkForSharedUnassignedTasks);
  }, [checkForSharedUnassignedTasks]);

  // ===== CONDITIONAL RETURNS - AFTER ALL HOOKS =====
  // Don't render anything if there are no users to filter
  if (!userFilters || userFilters.length === 0) {
    return null;
  }

  // ===== COMPONENT LOGIC =====
  // Calculate selected values from user filters
  const selectedUserValues = userFilters.filter(filter => filter.active).map(filter => filter.id);

  // Create the complete list of selected values including both user filters and special filters
  const selectedValues = isSharedUnassignedActive
    ? [...selectedUserValues, FILTER_CONSTANTS.SHARED_UNASSIGNED]
    : selectedUserValues;

  // Get button text based on selected users
  const getButtonText = () => {
    const activeUsers = userFilters.filter(filter => filter.active);
    const isSharedUnassignedActive = selectedValues.includes(FILTER_CONSTANTS.SHARED_UNASSIGNED);

    // No filters active
    if (activeUsers.length === 0 && !isSharedUnassignedActive) {
      return 'People';
    }
    // Only shared-unassigned active
    else if (activeUsers.length === 0 && isSharedUnassignedActive) {
      return 'Shared & unassigned';
    }
    // Only one user active
    else if (activeUsers.length === 1 && !isSharedUnassignedActive) {
      return formatUserDisplayName(activeUsers[0].name);
    }
    // Multiple filters active (users and/or shared-unassigned)
    else {
      // Count active filters (users + possible shared-unassigned)
      const totalActiveFilters = activeUsers.length + (isSharedUnassignedActive ? 1 : 0);
      return `People (${totalActiveFilters})`;
    }
  };

  // Handle clearing all filters
  const handleClear = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    // Clear all active user filters
    userFilters.forEach(filter => {
      if (filter.active) {
        onSelect(filter.id, false);
      }
    });

    // Clear the shared-unassigned filter if active
    if (selectedValues.includes(FILTER_CONSTANTS.SHARED_UNASSIGNED)) {
      onSelect(FILTER_CONSTANTS.SHARED_UNASSIGNED, false);
    }

    // Explicitly set dropdown to closed state
    setDropdownState(false);
  };

  // Custom button to show in the dropdown using the reusable FilterButton
  const userFilterButton = (
    <FilterButton
      isActive={isOpen}
      hasFilters={selectedValues.length > 0}
      onClick={() => setDropdownState(!isOpen)}
      onClear={selectedValues.length > 0 ? handleClear : undefined}
      label={getButtonText()}
      icon={<User size={16} className="text-blue-500" />}
      isMobile={actualMobile}
      className=""
    />
  );

  // Convert to format expected by FilterDropdown
  const userOptions = userFilters.map(filter => ({
    id: filter.id,
    label: formatUserDisplayName(filter.name),
    icon: <User size={16} className="text-blue-500" />, // Always use User icon for consistency
    // Use avatar as an additional visual element in the label
    avatar: filter.avatar_url ? (
      <img
        src={filter.avatar_url}
        alt={filter.name}
        className="w-6 h-6 rounded-full object-cover"
      />
    ) : (
      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
        {formatUserDisplayName(filter.name)?.charAt(0) || '?'}
      </div>
    ),
  }));

  // Create proper sections for the dropdown with section headers
  const sections = [
    {
      title: 'People',
      options: userOptions,
    },
    // Only show the "Other" section if there are shared & unassigned tasks
    ...(!hasSharedUnassignedTasks
      ? []
      : [
          {
            title: 'Other',
            options: [
              {
                id: FILTER_CONSTANTS.SHARED_UNASSIGNED,
                label: 'Shared & unassigned',
                icon: <UserX size={16} className="text-blue-500" />,
                avatar: (
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                    <UserX size={14} className="text-blue-500" />
                  </div>
                ),
              },
            ],
          },
        ]),
  ];

  /**
   * Handles changes to the selected filters
   * With our updated logic, all selections are exclusive
   */
  const handleChange = (newSelectedValues: string[]) => {
    // Calculate which IDs were added or removed
    const previousSelectedValues = selectedValues;

    // Special case for the shared-unassigned option
    if (
      newSelectedValues.includes(FILTER_CONSTANTS.SHARED_UNASSIGNED) ||
      previousSelectedValues.includes(FILTER_CONSTANTS.SHARED_UNASSIGNED)
    ) {
      // Calculate if shared-unassigned was toggled
      const wasSharedUnassignedSelected = previousSelectedValues.includes(FILTER_CONSTANTS.SHARED_UNASSIGNED);
      const isSharedUnassignedSelected = newSelectedValues.includes(FILTER_CONSTANTS.SHARED_UNASSIGNED);
      const sharedUnassignedToggled = wasSharedUnassignedSelected !== isSharedUnassignedSelected;

      if (sharedUnassignedToggled) {
        // If shared-unassigned was toggled, update it
        logger.component(`${isSharedUnassignedSelected ? 'Adding' : 'Removing'} shared-unassigned`);
        onSelect(FILTER_CONSTANTS.SHARED_UNASSIGNED, true);
        
        // Close dropdown if deselecting
        if (!isSharedUnassignedSelected) {
          setDropdownState(false);
        }
      }
      return;
    }

    // Regular handling for user IDs - always exclusive
    // This finds the single ID that was added or removed
    if (newSelectedValues.length > 0) {
      // Find the new ID that wasn't in previous selection
      const newlyAddedIds = newSelectedValues.filter(id => !previousSelectedValues.includes(id));
      
      if (newlyAddedIds.length > 0) {
        // If something was added, select it exclusively
        onSelect(newlyAddedIds[0], true);
      } else {
        // If nothing was added, something was removed
        const removedIds = previousSelectedValues.filter(id => !newSelectedValues.includes(id));
        if (removedIds.length > 0) {
          // Deselect the removed item
          onSelect(removedIds[0], true);
        }
      }
    } else if (previousSelectedValues.length === 1) {
      // Deselecting the only selected item
      onSelect(previousSelectedValues[0], true);
    }
    
    // Close the dropdown after selection
    setDropdownState(false);
  };

  // Don't render the button in mobile view - it will be in a menu
  if (actualMobile && !isOpen) {
    return null;
  }

  // Use the selectedValues directly, which should already include 'shared-unassigned' if active
  const combinedSelectedValues = selectedValues;

  return (
    <FilterDropdown
      sections={sections}
      selectedValues={combinedSelectedValues}
      onChange={handleChange}
      onClear={handleClear}
      allowMultiple={false}
      label="Users"
      placeholder="People"
      isMobile={actualMobile}
      open={isOpen}
      onOpenChange={setDropdownState}
      trigger={userFilterButton}
      buttonClassName={actualMobile ? '' : 'bg-transparent hover:bg-white'}
      className="z-[9999]"
    />
  );
};

export default DashboardUserFilterDropdown;
